import { Button, Icon, Progress, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Chart } from '@hero/atoms';
import { SkeletonLoaders } from '@hero/components';
import {
  CONTACT_TYPE,
  dateLabelFormatDayWithMonth,
  dateLabelFormatWithTime,
  FEATURE_TYPES,
  RECOGNITION_STATUSES,
  STALE_TIME
} from '@hero/constants';
import { GRAPH_DATA_TYPES } from '@hero/constants/graphData';
import { useSendRemindersNotification } from '@hero/hooks';
import { useSendRemindersRecognition } from '@hero/services';
import { useGetGraphData } from '@hero/services/order-service/hook';

import { TableProducts } from '../TableProducts';

import * as S from './style';

function ExpandedRecognitionRow({ row, onNavigateToDetailPage }) {
  const { send } = useSendRemindersNotification({ useSendReminder: useSendRemindersRecognition });
  const { data, isLoading } = useGetGraphData(
    {
      type: GRAPH_DATA_TYPES.ORDERS_ACCEPTED,
      associationType: FEATURE_TYPES.recognition,
      associationCode: row.original?.code,
      startDate: dayjs().subtract(6, 'day').startOf('day').format(),
      endDate: dayjs().endOf('day').format()
    },
    {
      staleTime: STALE_TIME.useGetGraphData,
      cacheTime: STALE_TIME.useGetGraphData
    }
  );

  const graphData = data?.graphData || [];

  const { t } = useTranslation();

  const renderProducts = () => {
    return <TableProducts products={row.original?.products || []} />;
  };

  const chartOptions = {
    chart: {
      type: 'bar',
      toolbar: {
        show: false
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '40%',
        distributed: false,
        rangeBarOverlap: true
      }
    },
    colors: ['var(--color-info-weaker)'],
    dataLabels: {
      enabled: false
    },
    grid: {
      show: true,
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    xaxis: {
      categories: graphData.map(({ date }) => dayjs(date).format(dateLabelFormatDayWithMonth)),
      axisBorder: {
        show: true
      },
      axisTicks: {
        show: true
      },
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
          colors: 'var(--color-fg-weak)'
        }
      }
    },
    yaxis: {
      min: 0,
      max: 20,
      tickAmount: 4,
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
          colors: 'var(--color-fg-weak)'
        }
      }
    },
    tooltip: {
      enabled: true,
      intersect: true,
      followCursor: true,
      onDatasetHover: {
        highlightDataSeries: false
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex][dataPointIndex];
        const date = w.globals.labels[dataPointIndex];

        return `
          <div style="padding: var(--size-8)">
            <div style="color: var(--color-fg-weak); font-size: 12px; margin-bottom: 4px">${date}</div>
            <div style="font-size: 14px">${value} ${t('redemptions')}</div>
          </div>
        `;
      },
      fixed: {
        enabled: false
      }
    },
    states: {
      hover: {
        filter: {
          type: 'darken',
          value: 0.85
        }
      }
    }
  };

  const chartSeries = [
    {
      name: 'Redemptions',
      data: graphData.map(({ value }) => Number(value || 0))
    }
  ];

  const maxCount = Number(row.original?.contacts?.maxCount);
  const redeemCount = Number(row.original?.contacts?.redeemCount);
  const acceptanceRate = Math.round((redeemCount / maxCount) * 100);

  if (isLoading) {
    return (
      <S.ExpandedRowWrapper>
        <SkeletonLoaders.ExpandedRecognitionRow />
      </S.ExpandedRowWrapper>
    );
  }

  return (
    <S.ExpandedRowWrapper>
      <S.ExpandedRowInnerWrapper>
        <S.SummaryWrapper>
          <Typography
            style={{
              marginBottom: 'var(--size-16)'
            }}
            variant="body2Bold"
          >
            {t('summary')}
          </Typography>
          <S.SummaryItem>
            <Typography variant="body2">{t('items')}:</Typography>
            <S.SummaryItemRight>{renderProducts()}</S.SummaryItemRight>
          </S.SummaryItem>
          <S.SummaryItem>
            <Typography variant="body2">{t('endDate')}:</Typography>
            <S.SummaryItemRight>
              <Typography variant="body2SemiBold">
                {dayjs(row.original?.settings?.expiresAt).format(dateLabelFormatWithTime)}
              </Typography>
            </S.SummaryItemRight>
          </S.SummaryItem>
          <S.SummaryItem>
            <Typography variant="body2">{t('giftsRedeemed')}:</Typography>
            <S.SummaryItemRight>
              <Icon name="giftbox-opened" color="var(--color-accent-primary-default)" />
              <Progress max={maxCount} value={redeemCount} />
              <Typography style={{ margin: '0 var(--size-8)' }} variant="body2Bold">
                {acceptanceRate}%
              </Typography>

              <Typography variant="body2">
                {redeemCount}/{maxCount}
              </Typography>
            </S.SummaryItemRight>
          </S.SummaryItem>
        </S.SummaryWrapper>
        <VerticalDivider
          style={{
            height: 220,
            margin: '0 var(--size-16)'
          }}
        />
        <S.ChartWrapper>
          <Chart
            chartOptions={chartOptions}
            chartSeries={chartSeries}
            title={t('redemptionActivity')}
            subtitle={t('last7Days')}
          />
        </S.ChartWrapper>
      </S.ExpandedRowInnerWrapper>
      <S.ExpandedRowBottomWrapper>
        <Button
          variant="text"
          onClick={() => {
            onNavigateToDetailPage(row);
          }}
        >
          {t('seeDetails')}
        </Button>
        {row.original?.status?.current === RECOGNITION_STATUSES.live &&
          row.original?.contacts.type !== CONTACT_TYPE.number && (
            <Button
              startIcon={<Icon name="email" />}
              onClick={() => send(row?.original?.code)}
              variant="outlined"
            >
              {t('remindRecipients')}
            </Button>
          )}
      </S.ExpandedRowBottomWrapper>
    </S.ExpandedRowWrapper>
  );
}

ExpandedRecognitionRow.propTypes = {
  row: PropTypes.object.isRequired,
  onNavigateToDetailPage: PropTypes.func.isRequired
};

export { ExpandedRecognitionRow };
